/**
 * Which corners of a bar segment get rounded, per series and category.
 *
 * ECharts applies `itemStyle.borderRadius` to a rect's *geometric* corners, in the order
 * [top-left, top-right, bottom-right, bottom-left]. The chart configs therefore carry a fixed array
 * that rounds the end a *positive* bar grows towards: [0, 4, 4, 0] (right) for the horizontal charts,
 * [4, 4, 0, 0] (top) for the vertical ones. A negative bar grows the other way, so that same array
 * rounds the end sitting on the axis and leaves the free end square — the bar reads as if it were
 * pointing the wrong way (RTLCA-5189). The radius has to follow the value's sign.
 *
 * In a stack only the outermost segment on each side of zero owns a free end. Inner segments stay
 * square so the bar reads as one continuous shape rather than a row of rounded bumps, and a stack
 * that crosses zero gets a rounded end on *both* sides: the last positive segment rounds away from
 * zero (right / up), the last negative segment rounds away from zero the other way (left / down).
 *
 * Note the deliberate difference from `getValueLabelAnchors`, which picks the *first* negative
 * series: that one wants the negative stack's edge nearest zero, because that is where the label
 * goes. This one wants its far end, because that is where the shape ends. Same data, opposite ends,
 * so please don't "align" the two.
 *
 * Kept as a dependency-free leaf module so it can be unit tested without pulling in ECharts or the
 * `@/features/Charts` barrel (which imports .vue components).
 */

/** Corner radii in ECharts order: [top-left, top-right, bottom-right, bottom-left]. */
export type BarCorners = [number, number, number, number];

export type BarOrientation = "horizontal" | "vertical";

/** Mirrors the radius the chart configs use, so an opted-in chart keeps the look it had. */
const DEFAULT_RADIUS = 4;

const square = (): BarCorners => [0, 0, 0, 0];

/**
 * The corners of the end a bar grows towards: right / left for a horizontal bar, top / bottom for a
 * vertical one. Zero counts as positive — ECharts still draws a `barMinHeight` stub for it, and
 * rounding that stub the same way as a positive bar is what the charts did before.
 */
export function getBarEndRadius(value: number, orientation: BarOrientation, radius = DEFAULT_RADIUS): BarCorners {
  if (orientation === "vertical") {
    return value < 0 ? [0, 0, radius, radius] : [radius, radius, 0, 0];
  }
  return value < 0 ? [radius, 0, 0, radius] : [0, radius, radius, 0];
}

/**
 * @param seriesValues values per series, each indexed by category — `seriesValues[series][category]`
 * @param categoryCount number of categories on the category axis
 * @param options `orientation` of the bars, whether they are `stacked`, and the corner `radius`
 * @returns corner radii per series and category — `radii[series][category]`
 */
export function getBarRadii(
  seriesValues: number[][],
  categoryCount: number,
  options: { orientation: BarOrientation; stacked?: boolean; radius?: number }
): BarCorners[][] {
  const { orientation, stacked = false, radius = DEFAULT_RADIUS } = options;

  // Side-by-side bars each have their own free end, so every segment rounds by its own sign.
  if (!stacked) {
    return seriesValues.map((values) =>
      Array.from({ length: categoryCount }, (_, categoryIndex) =>
        getBarEndRadius(values[categoryIndex] ?? 0, orientation, radius)
      )
    );
  }

  const radii = seriesValues.map(() => Array.from({ length: categoryCount }, square));

  for (let categoryIndex = 0; categoryIndex < categoryCount; categoryIndex++) {
    let lastPositive = -1;
    let lastNegative = -1;

    seriesValues.forEach((values, seriesIndex) => {
      const value = values[categoryIndex] ?? 0;
      if (value > 0) lastPositive = seriesIndex;
      else if (value < 0) lastNegative = seriesIndex;
    });

    // Nothing but zeros: ECharts still draws a `barMinHeight` stub, so keep the rounded end these
    // charts had before rather than turning the stub into a square nub.
    if (lastPositive < 0 && lastNegative < 0) {
      if (radii[0]) radii[0][categoryIndex] = getBarEndRadius(0, orientation, radius);
      continue;
    }

    if (lastPositive >= 0) radii[lastPositive][categoryIndex] = getBarEndRadius(1, orientation, radius);
    if (lastNegative >= 0) radii[lastNegative][categoryIndex] = getBarEndRadius(-1, orientation, radius);
  }

  return radii;
}
